import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import recognitionService from '@/services/financeRecognition.service';
import { apiErrorMessage } from './prepaidDisplay';

type DrillKind = 'line' | 'posting' | 'revision';

const TAB_FOR: Record<DrillKind, string> = { line: 'schedule', posting: 'journals', revision: 'adjustments' };

/** Resolves GL / notification references (schedule line, posting, adjustment) to the owning contract or run. */
const PrepaidDrillRedirectPage: React.FC<{ kind: DrillKind }> = ({ kind }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isError, error } = useQuery({
    queryKey: ['recognition', 'resolve', kind, id],
    queryFn: () => recognitionService.resolve(kind, id),
    enabled: Boolean(id),
    retry: false,
  });

  useEffect(() => {
    if (!data) return;
    if (kind === 'posting' && data.run_id) navigate(`/fin/prepaid/runs/${data.run_id}`, { replace: true });
    else if (data.contract_id) navigate(`/fin/prepaid/contracts/${data.contract_id}?tab=${TAB_FOR[kind]}`, { replace: true });
    else if (data.run_id) navigate(`/fin/prepaid/runs/${data.run_id}`, { replace: true });
  }, [data, kind, navigate]);

  if (isError || (data && !data.contract_id && !data.run_id)) {
    return (
      <Alert
        style={{ margin: 24 }}
        type="warning"
        showIcon
        message={t('prepaidRecordNotFound')}
        description={isError ? apiErrorMessage(error) : 'It may have been removed or belongs to another company.'}
        action={<Button onClick={() => navigate('/fin/prepaid')}>{t('openWorkspace')}</Button>}
      />
    );
  }
  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <Spin tip={t('openingPrepaidRecord')}>
        <div style={{ minHeight: 60 }} />
      </Spin>
    </div>
  );
};

export default PrepaidDrillRedirectPage;
